import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router";

import { ASSETS_BY_IDS_WITH_QUANTITY_QUERY } from "../../../constants/graphql/queries";
import { ADD_INVENTORY_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import BulkAddToCartTable from "./BulkAddToCartTable";

export default function BulkAddToOrderProgressModal({
  selectedIds,
  inventoryRequestId,
  selectedOrder,
  onClose,
  onComplete,
}) {
  const { projectId } = useParams();
  const { notifySuccess, notifyError } = useNotification();
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentAsset: "" });
  const [assetStatus, setAssetStatus] = useState({});
  const [quantityOverrides, setQuantityOverrides] = useState({});

  const handleQuantityChange = (assetId, qty) =>
    setQuantityOverrides(prev => ({ ...prev, [assetId]: qty }));

  const getEffectiveQty = asset =>
    quantityOverrides[asset.id] !== undefined ? quantityOverrides[asset.id] : asset.availableQuantity;

  const { loading: assetsLoading, data: assetsData } = useQuery(ASSETS_BY_IDS_WITH_QUANTITY_QUERY, {
    variables: { assetIds: selectedIds },
    skip: !selectedIds || selectedIds.length === 0,
    fetchPolicy: "network-only",
  });

  const rawAssets = assetsData?.assetsCollection?.collection || [];
  const assets = rawAssets.filter(asset => asset.availableQuantity > 0);
  const zeroQuantityAssets = rawAssets.filter(asset => asset.availableQuantity <= 0);
  const orderNumber = selectedOrder?.workOrderNumber;
  const projectName = selectedOrder?.project?.name || selectedOrder?.projectName;
  const orderDetails = [orderNumber && `Order #: ${orderNumber}`, projectName]
    .filter(Boolean)
    .join(", ");

  const handleAddToOrder = async () => {
    if (!assets.length) {
      notifyError("No assets found");
      onClose();
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: assets.length, currentAsset: "" });
    setAssetStatus({});

    const failed = [];
    for (let index = 0; index < assets.length; index++) {
      const asset = assets[index];
      setAssetStatus(prev => ({ ...prev, [asset.id]: "moving" }));
      setProgress({
        current: index + 1,
        total: assets.length,
        currentAsset: asset.assetId || asset.itemId || asset.id,
      });

      const qty = getEffectiveQty(asset);
      if (!qty || qty <= 0) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({ assetId: asset.assetId || asset.id, error: "No available quantity" });
        continue;
      }

      try {
        await addInventoryItem({
          variables: {
            assetId: asset.id,
            inventoryRequestId,
            quantity: qty,
          },
        });
        setAssetStatus(prev => ({ ...prev, [asset.id]: "moved" }));
      } catch (mutationError) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({
          assetId: asset.assetId || asset.id,
          error: mutationError.message || "Unknown error",
        });
      }
    }

    setProcessing(false);
    if (failed.length === 0) {
      notifySuccess(`Successfully added ${assets.length} assets to the order`);
      onComplete();
      onClose();
    } else {
      const successCount = assets.length - failed.length;
      if (successCount > 0) {
        notifySuccess(`Added ${successCount} assets. ${failed.length} failed.`);
        onComplete();
      } else {
        notifyError("Failed to add any assets to the order");
      }
    }
  };

  const totalAssets = assets.length || selectedIds.length;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontSize={20} fontWeight={500} color="textPrimary">
              Bulk Add to Order
            </Typography>
            <Chip label={totalAssets} size="small" color="primary" variant="outlined" />
          </Box>
          {orderDetails && (
            <Typography variant="body2" color="text.secondary" align="center">
              {orderDetails}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {assetsLoading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <LinearProgress sx={{ width: "100%", mb: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Fetching asset data for {totalAssets} selected assets...
            </Typography>
          </Box>
        )}
        {!assetsLoading && assets.length > 0 && (
          <BulkAddToCartTable
            assets={assets}
            statusMap={assetStatus}
            quantityOverrides={quantityOverrides}
            onQuantityChange={handleQuantityChange}
            processing={processing}
          />
        )}
        {!assetsLoading && zeroQuantityAssets.length > 0 && (
          <Box py={2} px={1} mt={assets.length > 0 ? 2 : 0}>
            {assets.length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 500 }}>
                {zeroQuantityAssets.length === 1
                  ? "1 item was excluded because it has no available quantity:"
                  : `${zeroQuantityAssets.length} items were excluded because they have no available quantity:`}
              </Typography>
            )}
            {assets.length === 0 && (
              <>
                <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 1 }}>
                  The following items have an available quantity of 0 and cannot be added to the
                  selected order.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
                  Click the icon to see why.
                </Typography>
              </>
            )}
            <List dense>
              {zeroQuantityAssets.map(asset => {
                const searchId = asset.itemId || asset.assetId;
                const href = projectId
                  ? `/projects/${projectId}/capture-inventory?search=${searchId}&inventoryPlacement=true&showQuantityCalculation=true`
                  : null;
                return (
                  <ListItem key={asset.id} divider>
                    <ListItemText
                      primary={searchId}
                      secondary={asset.description || "-"}
                    />
                    <ListItemSecondaryAction>
                      {href && (
                        <IconButton
                          edge="end"
                          size="small"
                          sx={{ color: "#1976d2" }}
                          onClick={() => window.open(href, "_blank")}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
        {!assetsLoading && rawAssets.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} px={2}>
            <Typography variant="body1" color="textSecondary" align="center">
              No assets found for the selected IDs.
            </Typography>
          </Box>
        )}
      </DialogContent>
      {processing && (
        <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!assetsLoading && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddToOrder}
            disabled={processing || assets.length === 0}
          >
            Add to Order
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
